import { ThemeProvider } from "expo-router/react-navigation";
import { AppContext } from '@/components/AppContext';
import { createContext, useCallback, useContext, useState, PropsWithChildren, useEffect } from 'react';
import { GestureResponderEvent, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import CustomActivityIndicator from '@/components/CustomActivityIndicator';
import * as AppCore from '@/utilities/AppCore';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppUtilities from '@/utilities/AppUtilities';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setLoggedIn } from '@/state/user';
import { CustomSafeAreaView } from '@/components/CustomSafeAreaView';
import { CustomKeyboardAvoidingView } from '@/components/CustomKeyboardAvoidingView';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Slot } from 'expo-router';
import { CustomErrorBoundary } from "@/components/CustomErrorBoundary";
import { ErrorBoundaryProps } from 'expo-router';
import { useColorScheme } from '@/components/useColorScheme';
import { DarkTransparentTheme, LightTransparentTheme } from '@/components/Themes';

// https://docs.expo.dev/router/error-handling/
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  const dispatch = useDispatch();
  const onDismiss = useCallback(async () => {
    // To be safe, we'll log the user out.
    try {
      await AppUtils.logout(dispatch, undefined, undefined);
    } catch (e) {
      AppUtilities.handleException(error, "(stateful)/ErrorBoundary.onDismiss");
    }

    retry();
  }, [error, dispatch]);

  const wasLoggedIn = AppCore.isLoggedIn();

  return (
    <CustomErrorBoundary
      title="Fatal error"
      errorMessage={"A fatal error has occurred: " + AppUtilities.getErrors(error)}
      dismissText="OK"
      onDismiss={onDismiss}
      error={error}
      extraLine={wasLoggedIn ? "Press OK to continue (just in case, we'll log you out and you can log back in)." : undefined}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

const EXPLOSION_WIDTH = 142;
const EXPLOSION_HEIGHT = 200;

export default function LayoutContextful() {
  const [isExplosionVisible, setIsExplosionVisible] = useState(false);
  const [explosionLeft, setExplosionLeft] = useState(50);
  const [explosionTop, setExplosionTop] = useState(0);
  const [showActivity, setShowActivity] = useState(false);
  const dispatch = useDispatch();
  const colorScheme = useColorScheme();

  const showSpinner = () => {
    setShowActivity(true);
  };

  const hideSpinner = () => {
    setShowActivity(false);
  };

  const explode = (evt: GestureResponderEvent) => {
    setExplosionLeft(evt.nativeEvent.pageX - (EXPLOSION_WIDTH / 2));
    setExplosionTop(evt.nativeEvent.pageY - (EXPLOSION_HEIGHT / 2));
    setIsExplosionVisible(true);
    setTimeout(() => {
      setIsExplosionVisible(false);
    }, 700);
  };

  return (
    <AppContext value={{ explode, setShowActivity, showSpinner, hideSpinner }}>
      <ThemeProvider value={colorScheme === 'dark' ? DarkTransparentTheme : LightTransparentTheme}>
        <View style={styles.container}>
          <Slot />
        </View>

        {isExplosionVisible && (
          <Image
            source={require('@/assets/images/explosion2.gif')}
            style={{
              position: 'absolute',
              top: explosionTop,
              left: explosionLeft,
              width: EXPLOSION_WIDTH,
              height: EXPLOSION_HEIGHT,
            }}
          />
        )}

        {showActivity && (
          <CustomActivityIndicator
            absoluteCenter={true}
            dark={true}
          />
        )}
      </ThemeProvider>
    </AppContext>
  );
};
